/** modified version of
 * @see https://react-day-picker.js.org/guides/input-fields#using-the-useinput-hook
 */

import {
    format as _format,
    differenceInCalendarDays,
    parse,
    startOfDay,
} from "date-fns";
import { enUS } from "date-fns/locale";
import React, { useCallback, useMemo, useState } from "react";
import {
    addToRange,
    DateRange,
    DayClickEventHandler,
    DayPickerBase,
    DayPickerRangeProps,
    DayPickerSingleProps,
    isDateRange,
    MonthChangeEventHandler,
} from "react-day-picker";
import { CalendarValue, parseFromToProps } from "../shared/calendar-utils";

export function isValidDate(day?: Date): boolean {
    return !!day && !isNaN(day.getTime());
}

/** The props to attach to the input field when using {@link useInput}. */
export type InputHTMLAttributes = Pick<
    React.InputHTMLAttributes<HTMLInputElement>,
    "onBlur" | "onChange" | "onFocus" | "value" | "placeholder"
>;

/** The props to attach to the DayPicker component when using {@link useInput}. */
export type InputSingleDayPickerProps = Pick<
    DayPickerSingleProps,
    | "fromDate"
    | "toDate"
    | "locale"
    | "month"
    | "onDayClick"
    | "onMonthChange"
    | "selected"
    | "today"
    | "mode"
>;

/** The props to attach to the DayPicker component when using {@link useInput}. */
export type InputRangeDayPickerProps = Pick<
    DayPickerRangeProps,
    | "fromDate"
    | "toDate"
    | "locale"
    | "month"
    | "onDayClick"
    | "onMonthChange"
    | "selected"
    | "today"
    | "mode"
>;

export interface UseInputOptions
    extends Pick<
        DayPickerBase,
        | "locale"
        | "fromDate"
        | "toDate"
        | "fromMonth"
        | "toMonth"
        | "fromYear"
        | "toYear"
        | "today"
    > {
    /** The initially selected date */
    defaultSelected?: CalendarValue;
    /**
     * The format string for formatting the input field. See
     * https://date-fns.org/docs/format for a list of format strings.
     *
     * @defaultValue PP
     */
    format?: string;
    /** Make the selection required. */
    required?: boolean;
    /** Use range selection */
    isRangeSelect?: boolean;
    /** Value to display while user is not typing */
    placeholder?: string;
    onCalendarChange?: (obj: {
        date?: CalendarValue;
        updated?: boolean;
        inputValue?: string;
    }) => void;
}

/** Represent the value returned by {@link useInput}. */
export interface UseInputValue {
    /** The props to pass to a DayPicker component. */
    dayPickerProps: InputSingleDayPickerProps | InputRangeDayPickerProps;
    /** The props to pass to an input field. */
    inputProps: InputHTMLAttributes;
    /** A function to reset to the initial state. */
    reset: () => void;
    /** A function to set the selected day. */
    setSelected: (day: Date | DateRange | undefined) => void;
}

/** Return props and setters for binding an input field to DayPicker. */
export function useInput(options: UseInputOptions = {}): UseInputValue {
    const {
        locale = enUS,
        format = "PP",
        defaultSelected,
        isRangeSelect = false,
        required,
        onCalendarChange,
    } = options;
    const { fromDate, toDate } = parseFromToProps(options);
    const today = useMemo(
        () => startOfDay(options.today ?? new Date()),
        [options.today],
    );

    // Shortcut to the DateFns functions
    const parseValue = (value: string) => {
        // one day maybe we'll allow passing an array of allowed dates.
        // but according to this calendar component, that's not today.
        const yyFormat = parse(value, "MM/dd/yy", today, {
            locale,
        });
        const baseFormat = parse(value, format, today, {
            locale,
        });
        return isValidDate(yyFormat) ? yyFormat : baseFormat;
    };

    const getMonth = (date: Date | DateRange | undefined) => {
        if (!date) {
            return undefined;
        }
        if (isDateRange(date)) {
            return date.from ?? date.to;
        } else {
            return date;
        }
    };

    const formatInput = useCallback(
        (date: Date | DateRange | undefined) => {
            if (!date) {
                return "";
            } else if (!isDateRange(date) && isValidDate(date)) {
                return _format(date, format, { locale });
            } else if (isDateRange(date)) {
                const dateFrom = date.from
                    ? _format(date.from, format, { locale })
                    : format;
                const dateTo = date.to
                    ? _format(date.to, format, { locale })
                    : format;
                return `${dateFrom}-${dateTo}`;
            } else {
                return "";
            }
        },
        [format, locale],
    );

    // Initialize states
    const [month, setMonth] = useState(getMonth(defaultSelected) ?? today);
    const [selectedDay, setSelectedDay] = useState(defaultSelected);
    const defaultInputValue = formatInput(defaultSelected) || "";
    const [inputValue, setInputValue] = useState(defaultInputValue);
    const [hasFocus, setHasFocus] = useState(false);

    const reset = () => {
        setSelectedDay(defaultSelected);
        setMonth(getMonth(defaultSelected) ?? today);
        setInputValue(defaultInputValue);
        onCalendarChange?.({
            date: defaultSelected,
            updated: true,
            inputValue: defaultInputValue,
        });
    };

    const setSelected = useCallback(
        (date: Date | DateRange | undefined) => {
            if (inputValue !== formatInput(date)) {
                setSelectedDay(date);
                setMonth(getMonth(date) ?? today);
                if (!hasFocus) {
                    setInputValue(formatInput(date));
                }
            }
        },
        [formatInput, inputValue, setMonth, setSelectedDay, hasFocus, today],
    );

    const handleDayClick: DayClickEventHandler = (day, { selected }) => {
        if (selected) {
            setSelectedDay(undefined);
            setInputValue("");
            return;
        }
        if (isRangeSelect && (isDateRange(selectedDay) || !selectedDay)) {
            const newRange = addToRange(day, selectedDay as any);
            setSelectedDay(newRange);
            setInputValue(formatInput(newRange));
        } else if (!isRangeSelect && isValidDate(day)) {
            setSelectedDay(day);
            setInputValue(formatInput(day));
        }
    };

    const handleMonthChange: MonthChangeEventHandler = (month) => {
        setMonth(month);
    };

    const getRangeFromInput = (dateRangeStr: string): DateRange => {
        const dateArr = dateRangeStr.split("-");
        const dayFrom = parseValue(dateArr[0]);
        const dayTo = parseValue(dateArr[1]);
        return { from: dayFrom, to: dayTo };
    };

    const isInRange = (date: Date): boolean =>
        (!fromDate || differenceInCalendarDays(fromDate, date) <= 0) &&
        (!toDate || differenceInCalendarDays(date, toDate) <= 0);

    const isValidRange = (dateRangeStr: string) => {
        const { from, to } = getRangeFromInput(dateRangeStr);

        const isValidFromDate = from && isValidDate(from) && isInRange(from);
        const isValidToDate = to && isValidDate(to) && isInRange(to);

        return (
            isValidFromDate &&
            isValidToDate &&
            differenceInCalendarDays(to, from) > 0
        );
    };

    // When changing the input field, save its value in state and check if the
    // string is a valid date. If it is a valid day, set it as selected and update
    // the calendar’s month.
    const handleChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
        const targetValue = e.target.value;
        setInputValue(targetValue);
        if (targetValue === "") {
            setSelectedDay(undefined);
            onCalendarChange?.({
                date: undefined,
                updated: true,
                inputValue: targetValue,
            });
            return;
        }
        if (isRangeSelect) {
            const { from, to } = getRangeFromInput(targetValue);

            const isValidFromDate =
                from && isValidDate(from) && isInRange(from);
            const isValidToDate = to && isValidDate(to) && isInRange(to);
            if (from && to && isValidRange(targetValue)) {
                setSelectedDay({ from, to });
                setMonth(from);
                onCalendarChange?.({
                    date: { from, to },
                    updated: true,
                    inputValue: targetValue,
                });
            } else if (from && isValidFromDate) {
                setSelectedDay({ from, to: undefined });
                setMonth(from);
                onCalendarChange?.({
                    date: { from, to: undefined },
                    updated: true,
                    inputValue: targetValue,
                });
            } else if (to && isValidToDate) {
                setSelectedDay({ from: undefined, to });
                setMonth(to);
                onCalendarChange?.({
                    date: { from: undefined, to },
                    updated: true,
                    inputValue: targetValue,
                });
            } else {
                setSelectedDay(undefined);
                onCalendarChange?.({
                    date: undefined,
                    updated: false,
                    inputValue: targetValue,
                });
            }
        } else {
            const day = parseValue(targetValue);
            const isBefore =
                fromDate && differenceInCalendarDays(fromDate, day) > 0;
            const isAfter = toDate && differenceInCalendarDays(day, toDate) > 0;
            if (!isValidDate(day) || isBefore || isAfter) {
                setSelectedDay(undefined);
                onCalendarChange?.({
                    date: undefined,
                    updated: false,
                    inputValue: targetValue,
                });
                return;
            }
            setSelectedDay(day);
            setMonth(day);
            onCalendarChange?.({
                date: day,
                updated: true,
                inputValue: targetValue,
            });
        }
    };

    // Special case for _required_ fields: on blur, if the value of the input is not
    // a valid date, reset the calendar and the input value.
    const handleBlur: React.FocusEventHandler<HTMLInputElement> = () => {
        setHasFocus(false);
        if (!inputValue) {
            required && reset();
        } else if (
            (isRangeSelect && !isValidRange(inputValue)) ||
            (!isRangeSelect && !isValidDate(parseValue(inputValue)))
        ) {
            reset();
        } else if (isRangeSelect) {
            const { from, to } = getRangeFromInput(inputValue);
            if (isValidDate(from) || isValidDate(to)) {
                setMonth(from || to || today);
            }
        } else {
            const day = parseValue(inputValue);
            if (isValidDate(day)) {
                setMonth(day);
            }
        }
    };

    // When focusing, make sure DayPicker visualizes the month of the date in the
    // input field.
    const handleFocus: React.FocusEventHandler<HTMLInputElement> = () => {
        setHasFocus(true);
    };

    const singleDayPickerProps: InputSingleDayPickerProps = {
        month: month,
        onDayClick: handleDayClick,
        onMonthChange: handleMonthChange,
        selected: !isDateRange(selectedDay) ? selectedDay : undefined,
        locale,
        fromDate,
        toDate,
        today,
        mode: "single",
    };

    const rangeDayPickerProps: InputRangeDayPickerProps = {
        month: month,
        onDayClick: handleDayClick,
        onMonthChange: handleMonthChange,
        selected: isDateRange(selectedDay) ? selectedDay : undefined,
        locale,
        fromDate,
        toDate,
        today,
        mode: "range",
    };

    const dayPickerProps = isRangeSelect
        ? rangeDayPickerProps
        : singleDayPickerProps;

    const inputProps: InputHTMLAttributes = {
        onBlur: handleBlur,
        onChange: handleChange,
        onFocus: handleFocus,
        value: inputValue,
        placeholder: _format(new Date(), format, { locale }),
    };

    return { dayPickerProps, inputProps, reset, setSelected };
}
